import {
  clearOrquestadorSession,
  getAccessLevelFromGroups,
  getOrquestadorSession,
  ORQUESTADOR_AUTH_API_BASE_URL,
  storeOrquestadorSession,
} from './orquestadorAccess';

let hasValidatedCurrentPageLoadSession = false;
let currentPageLoadValidationPromise = null;

async function parseJsonResponse(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function requestOrquestadorToken(username, password) {
  const response = await fetch(`${ORQUESTADOR_AUTH_API_BASE_URL}/api/token/`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      password,
    }),
  });

  const data = await parseJsonResponse(response);

  if (!response.ok) {
    const error = new Error('INVALID_CREDENTIALS');
    error.status = response.status;
    throw error;
  }

  if (!data?.access || !data?.refresh) {
    throw new Error('INVALID_TOKEN_RESPONSE');
  }

  return data;
}

async function requestOrquestadorGroups(accessToken) {
  const response = await fetch(`${ORQUESTADOR_AUTH_API_BASE_URL}/api/token/is-valid/`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const data = await parseJsonResponse(response);

  if (!response.ok) {
    const error = new Error('INVALID_ACCESS_TOKEN');
    error.status = response.status;
    throw error;
  }

  if (!Array.isArray(data?.groups)) {
    throw new Error('INVALID_GROUPS_RESPONSE');
  }

  return data.groups;
}

async function requestOrquestadorTokenRefresh(refreshToken) {
  const response = await fetch(`${ORQUESTADOR_AUTH_API_BASE_URL}/api/token/refresh/`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refresh: refreshToken,
    }),
  });

  const data = await parseJsonResponse(response);

  if (!response.ok) {
    const error = new Error('INVALID_REFRESH_TOKEN');
    error.status = response.status;
    throw error;
  }

  if (!data?.access) {
    throw new Error('INVALID_REFRESH_RESPONSE');
  }

  return {
    access: data.access,
    refresh: data.refresh || refreshToken,
  };
}

function persistValidatedSession(session, accessToken, refreshToken, groups, storage) {
  const access = getAccessLevelFromGroups(groups);

  if (!access) {
    clearOrquestadorSession(storage);
    return null;
  }

  const nextSession = {
    ...session,
    access,
    accessToken,
    refreshToken,
    groups,
  };

  storeOrquestadorSession(nextSession, undefined, storage);

  return nextSession;
}

async function refreshAndValidateOrquestadorSession(session, storage) {
  if (!session?.refreshToken) {
    clearOrquestadorSession(storage);
    return null;
  }

  try {
    const tokenData = await requestOrquestadorTokenRefresh(session.refreshToken);
    const groups = await requestOrquestadorGroups(tokenData.access);

    return persistValidatedSession(session, tokenData.access, tokenData.refresh, groups, storage);
  } catch {
    clearOrquestadorSession(storage);
    return null;
  }
}

export async function authenticateOrquestadorUser(username, password) {
  const cleanUsername = username.trim();
  const tokenData = await requestOrquestadorToken(cleanUsername, password);
  const groups = await requestOrquestadorGroups(tokenData.access);

  return {
    username: cleanUsername,
    accessToken: tokenData.access,
    refreshToken: tokenData.refresh,
    groups,
  };
}

export async function validateStoredOrquestadorSession(storage = window.localStorage) {
  const session = getOrquestadorSession(storage);

  if (!session) {
    return null;
  }

  if (!session.accessToken) {
    return refreshAndValidateOrquestadorSession(session, storage);
  }

  try {
    const groups = await requestOrquestadorGroups(session.accessToken);

    return persistValidatedSession(session, session.accessToken, session.refreshToken, groups, storage);
  } catch {
    return refreshAndValidateOrquestadorSession(session, storage);
  }
}

export function markOrquestadorSessionValidatedForCurrentPageLoad() {
  hasValidatedCurrentPageLoadSession = true;
  currentPageLoadValidationPromise = null;
}

export function resetOrquestadorSessionValidatedForCurrentPageLoad() {
  hasValidatedCurrentPageLoadSession = false;
  currentPageLoadValidationPromise = null;
}

export function isOrquestadorSessionValidatedForCurrentPageLoad() {
  return hasValidatedCurrentPageLoadSession;
}

export function ensureValidOrquestadorSessionForCurrentPageLoad(storage = window.localStorage) {
  if (hasValidatedCurrentPageLoadSession) {
    return Promise.resolve(getOrquestadorSession(storage));
  }

  if (!currentPageLoadValidationPromise) {
    currentPageLoadValidationPromise = validateStoredOrquestadorSession(storage)
      .then((session) => {
        hasValidatedCurrentPageLoadSession = true;
        return session;
      });
  }

  return currentPageLoadValidationPromise;
}
